import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { THEME, getSavedThemeMode, getSavedAccentColor } from '../../utils/theme';
import api, { urlBase64ToUint8Array } from '../../utils/api';
import Spinner from '../ui/Spinner';
import Badge from '../ui/Badge';
import Avatar from '../ui/Avatar';
import SoundService from '../../utils/sounds';

const SettingsModal = ({ user, currentSquad, onClose, onUpdate, savedCredentials, onSync, onLogout, syncing, onThemeChange }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [displayName, setDisplayName] = useState(user.display_name || '');
  const [steamVanityUrl, setSteamVanityUrl] = useState(user.steam_vanity_url || '');
  const [color, setColor] = useState(user.color || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [themeMode, setThemeMode] = useState(getSavedThemeMode());
  const [accentColor, setAccentColor] = useState(getSavedAccentColor());
  const [squads, setSquads] = useState([]);
  const [myInvites, setMyInvites] = useState([]);
  const [loadingSquads, setLoadingSquads] = useState(false);
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [newInviteCode, setNewInviteCode] = useState(null);
  const [discordWebhookUrl, setDiscordWebhookUrl] = useState(user.discord_webhook_url || '');
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [twitchUsername, setTwitchUsername] = useState(user.twitch_username || '');
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(true);
  const [discordStatus, setDiscordStatus] = useState(null);
  const [discordLoading, setDiscordLoading] = useState(true);
  const [discordGuilds, setDiscordGuilds] = useState([]);
  const [creatingVoice, setCreatingVoice] = useState(false);
  const [oauthPolling, setOauthPolling] = useState(null); // Track OAuth polling interval

  // Check Discord OAuth status on mount
  useEffect(() => {
    const checkDiscordStatus = async () => {
      try {
        const result = await api.get('/api/integrations/discord/oauth/status');
        setDiscordStatus(result);
      } catch (err) { console.error('Failed to check Discord status:', err); }
      finally { setDiscordLoading(false); }
    };
    checkDiscordStatus();
  }, []);

  // Cleanup OAuth polling on unmount
  useEffect(() => {
    return () => {
      if (oauthPolling) clearInterval(oauthPolling);
    };
  }, [oauthPolling]);

  // Poll for OAuth status after opening popup
  const startOAuthPolling = (type) => {
    if (oauthPolling) clearInterval(oauthPolling);
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      if (attempts > 30) { // Stop after 60 seconds (30 * 2s)
        clearInterval(interval);
        setOauthPolling(null);
        return;
      }
      try {
        if (type === 'discord') {
          const result = await api.get('/api/integrations/discord/oauth/status');
          if (result.linked) {
            setDiscordStatus(result);
            setSuccess(`Discord linked as ${result.discord_username}!`);
            clearInterval(interval);
            setOauthPolling(null);
          }
        }
      } catch (err) { /* ignore polling errors */ }
    }, 2000);
    setOauthPolling(interval);
  };

  // Check push notification status on mount
  useEffect(() => {
    const checkPushStatus = async () => {
      try {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
          setPushLoading(false);
          return;
        }
        const result = await api.get('/api/push/status');
        setPushEnabled(result.subscribed);
      } catch (err) { console.error('Failed to check push status:', err); }
      finally { setPushLoading(false); }
    };
    checkPushStatus();
  }, []);

  const handleTogglePush = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setError('Push notifications not supported in this browser');
      return;
    }
    setPushLoading(true);
    setError('');
    try {
      if (pushEnabled) {
        // Unsubscribe
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) await subscription.unsubscribe();
        await api.delete('/api/push/unsubscribe');
        setPushEnabled(false);
        setSuccess('Push notifications disabled');
      } else {
        // Subscribe
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          setError('Notification permission denied');
          setPushLoading(false);
          return;
        }
        const vapidResponse = await api.get('/api/push/vapid-public-key');
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidResponse.publicKey)
        });
        const json = subscription.toJSON();
        await api.post('/api/push/subscribe', {
          endpoint: json.endpoint,
          p256dh: json.keys.p256dh,
          auth: json.keys.auth
        });
        setPushEnabled(true);
        setSuccess('Push notifications enabled!');
      }
    } catch (err) { setError(err.message || 'Failed to toggle push notifications'); }
    finally { setPushLoading(false); }
  };

  const handleSaveProfile = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const updated = await api.put('/api/profile', { display_name: displayName, steam_vanity_url: steamVanityUrl, color: color || null });
      setSuccess('Profile updated!');
      onUpdate(updated);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    if (newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    setSaving(true); setError(''); setSuccess('');
    try {
      await api.post('/api/change-password', { current_password: currentPassword, new_password: newPassword });
      // Refresh the session cookie with the new password (preserve rememberMe).
      await api.login(savedCredentials.username, newPassword, savedCredentials.rememberMe || false);
      setSuccess('Password changed!');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const loadSquadsData = useCallback(async () => {
    setLoadingSquads(true);
    try {
      const [squadsData, invitesData] = await Promise.all([
        api.get('/api/squads'),
        api.get('/api/invites')
      ]);
      setSquads(squadsData);
      setMyInvites(invitesData);
    } catch (err) { console.error('Failed to load squads:', err); }
    finally { setLoadingSquads(false); }
  }, []);

  useEffect(() => {
    if (activeTab === 'squads') loadSquadsData();
  }, [activeTab, loadSquadsData]);

  const handleCreateInvite = async (squadId) => {
    setCreatingInvite(true);
    try {
      const result = await api.post('/api/invites', { squad_id: squadId });
      setNewInviteCode(result.code);
      setSuccess(`Invite created! Code: ${result.code}`);
      loadSquadsData();
    } catch (err) { setError(err.message); }
    finally { setCreatingInvite(false); }
  };

  const handleRevokeInvite = async (code) => {
    try {
      await api.delete(`/api/invites/${code}`);
      setSuccess('Invite revoked');
      loadSquadsData();
    } catch (err) { setError(err.message); }
  };

  const copyInviteCode = (code) => {
    navigator.clipboard.writeText(code);
    setSuccess('Invite code copied to clipboard!');
  };

  const handleSaveWebhook = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await api.put('/api/profile', { discord_webhook_url: discordWebhookUrl || null });
      setSuccess(discordWebhookUrl ? 'Discord webhook saved!' : 'Discord webhook removed!');
      onUpdate({ discord_webhook_url: discordWebhookUrl || null });
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleTestWebhook = async () => {
    setTestingWebhook(true); setError(''); setSuccess('');
    try {
      await api.post('/api/test-webhook', {});
      setSuccess('Test message sent to Discord!');
    } catch (err) { setError(err.message); }
    finally { setTestingWebhook(false); }
  };

  const myColor = user.theme_color || THEME.success;
  const inputStyle = { width: '100%', background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: '10px', padding: '14px 16px', color: THEME.text, fontSize: '14px', boxSizing: 'border-box', outline: 'none', transition: 'border-color 0.2s' };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(5,5,10,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(20px) saturate(180%)', WebkitBackdropFilter: 'blur(20px) saturate(180%)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: -10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: -10 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        style={{ background: `linear-gradient(165deg, ${THEME.bgCard} 0%, ${THEME.bgElevated} 100%)`, borderRadius: '24px', padding: '28px', width: '95%', maxWidth: '480px', maxHeight: '90vh', overflow: 'auto', border: `1px solid ${THEME.glassBorder}`, boxShadow: THEME.modalShadow }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, color: THEME.text, fontSize: '22px', fontWeight: '700' }}>Settings</h2>
          <motion.button
            whileHover={{ scale: 1.1, backgroundColor: THEME.bgActive }}
            whileTap={{ scale: 0.95 }}
            onClick={onClose}
            style={{ background: THEME.bgHover, border: `1px solid ${THEME.glassBorder}`, color: THEME.textMuted, width: '36px', height: '36px', borderRadius: '10px', fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >&times;</motion.button>
        </div>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', background: THEME.bg, padding: '4px', borderRadius: '10px', flexWrap: 'wrap' }}>
          {['profile', 'squads', 'theme', 'integrations', 'account'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              flex: 1, minWidth: '60px', background: activeTab === tab ? myColor : 'transparent', color: activeTab === tab ? '#000' : THEME.textMuted,
              border: 'none', borderRadius: '8px', padding: '10px 6px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s', textTransform: 'capitalize', fontSize: '13px'
            }}>{tab === 'integrations' ? '🔗' : tab}</button>
          ))}
        </div>

        {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '10px', padding: '14px', marginBottom: '16px', color: THEME.danger, fontSize: '14px' }}>{error}</div>}
        {success && <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: '10px', padding: '14px', marginBottom: '16px', color: THEME.success, fontSize: '14px' }}>{success}</div>}

        {activeTab === 'profile' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {/* Avatar Section */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', padding: '20px', background: THEME.bg, borderRadius: '14px' }}>
              <Avatar src={user.avatar_url} name={user.display_name} size={80} color={myColor} />
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: THEME.text, fontSize: '18px', fontWeight: '600' }}>{user.display_name}</div>
                <div style={{ color: THEME.textMuted, fontSize: '13px' }}>@{user.username}</div>
              </div>
              <button
                onClick={async () => {
                  setSaving(true); setError(''); setSuccess('');
                  try {
                    const result = await api.post('/api/refresh-avatar', {});
                    if (result.avatar_url) {
                      onUpdate({ avatar_url: result.avatar_url });
                      setSuccess('Avatar refreshed from Steam!');
                    } else {
                      setError('Could not fetch avatar. Make sure your Steam profile is public.');
                    }
                  } catch (err) { setError(err.message); }
                  finally { setSaving(false); }
                }}
                disabled={saving}
                style={{
                  background: 'transparent', border: `1px solid ${myColor}`, borderRadius: '8px',
                  padding: '8px 16px', color: myColor, fontSize: '12px', fontWeight: '600',
                  cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
                }}
              >
                🔄 {saving ? 'Refreshing...' : 'Refresh from Steam'}
              </button>
            </div>
            <div>
              <label style={{ display: 'block', color: THEME.textMuted, fontSize: '12px', marginBottom: '8px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Display Name</label>
              <input type="text" value={displayName} onChange={e => setDisplayName(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={{ display: 'block', color: THEME.textMuted, fontSize: '12px', marginBottom: '8px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Steam Vanity URL</label>
              <input type="text" value={steamVanityUrl} onChange={e => setSteamVanityUrl(e.target.value)} placeholder="e.g. jasonomega" style={inputStyle} />
              <p style={{ color: THEME.textDim, fontSize: '11px', margin: '6px 0 0' }}>steamcommunity.com/id/<strong style={{ color: THEME.textMuted }}>{steamVanityUrl || '...'}</strong></p>
            </div>
            <div>
              <label style={{ display: 'block', color: THEME.textMuted, fontSize: '12px', marginBottom: '8px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Theme Color</label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input type="color" value={color || myColor} onChange={e => setColor(e.target.value)} style={{ width: '52px', height: '52px', border: 'none', borderRadius: '10px', cursor: 'pointer', background: 'none' }} />
                <input type="text" value={color} onChange={e => setColor(e.target.value)} placeholder="#f97316" style={{ ...inputStyle, flex: 1 }} />
              </div>
            </div>
            <div style={{ padding: '14px', background: THEME.bg, borderRadius: '10px' }}>
              <div style={{ color: THEME.textMuted, fontSize: '11px', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Steam ID</div>
              <div style={{ color: THEME.text, fontFamily: 'monospace', fontSize: '14px' }}>{user.steam_id || 'Not resolved yet'}</div>
            </div>
            <button onClick={handleSaveProfile} disabled={saving} style={{ background: myColor, color: '#000', border: 'none', borderRadius: '10px', padding: '14px', fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '15px' }}>{saving ? 'Saving...' : 'Save Profile'}</button>
          </div>
        )}

        {activeTab === 'squads' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {loadingSquads ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spinner size={24} color={myColor} />
                <p style={{ color: THEME.textMuted, marginTop: '12px' }}>Loading squads...</p>
              </div>
            ) : (
              <>
                {/* My Squads */}
                <div>
                  <h3 style={{ margin: '0 0 12px', color: THEME.text, fontSize: '15px', fontWeight: '600' }}>My Squads</h3>
                  {squads.length === 0 ? (
                    <p style={{ color: THEME.textMuted, fontSize: '13px' }}>You're not in any squads yet.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {squads.map(squad => (
                        <div key={squad.id} style={{ background: THEME.bg, borderRadius: '12px', padding: '16px', border: `1px solid ${THEME.border}` }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                            <span style={{ fontSize: '24px' }}>{squad.icon}</span>
                            <div>
                              <div style={{ color: THEME.text, fontWeight: '600', fontSize: '15px' }}>{squad.name}</div>
                              <div style={{ color: THEME.textMuted, fontSize: '12px' }}>{squad.members?.length || 0} members</div>
                            </div>
                            {squad.is_founder_squad && <Badge color={THEME.omega} bg={`${THEME.omega}20`}>Founder</Badge>}
                          </div>
                          {squad.members && (
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                              {squad.members.map(m => (
                                <div key={m.user_id} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: m.theme_color ? `${m.theme_color}20` : THEME.bgHover, padding: '4px 10px 4px 4px', borderRadius: '20px' }}>
                                  <Avatar src={m.avatar_url} name={m.display_name} size={22} color={m.theme_color || THEME.text} />
                                  <span style={{ color: m.theme_color || THEME.text, fontSize: '12px', fontWeight: '500' }}>
                                    {m.is_founder && '⭐ '}{m.display_name}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                          <button onClick={() => handleCreateInvite(squad.id)} disabled={creatingInvite || (user.invites_remaining !== null && user.invites_remaining <= 0)} style={{ background: myColor, color: '#000', border: 'none', borderRadius: '8px', padding: '8px 14px', fontSize: '12px', fontWeight: '600', cursor: (creatingInvite || (user.invites_remaining !== null && user.invites_remaining <= 0)) ? 'not-allowed' : 'pointer', opacity: (user.invites_remaining !== null && user.invites_remaining <= 0) ? 0.5 : 1 }}>
                            {creatingInvite ? 'Creating...' : '+ Create Invite'}
                          </button>
                          {user.invites_remaining !== null && <span style={{ marginLeft: '10px', color: THEME.textMuted, fontSize: '11px' }}>({user.invites_remaining} invites left)</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* My Invites */}
                <div>
                  <h3 style={{ margin: '0 0 12px', color: THEME.text, fontSize: '15px', fontWeight: '600' }}>Active Invites</h3>
                  {myInvites.length === 0 ? (
                    <p style={{ color: THEME.textMuted, fontSize: '13px' }}>No active invites.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {myInvites.map(invite => (
                        <div key={invite.code} style={{ background: THEME.bg, borderRadius: '10px', padding: '12px', border: `1px solid ${THEME.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontFamily: 'monospace', color: THEME.text, fontSize: '14px', fontWeight: '600' }}>{invite.code}</div>
                            <div style={{ color: THEME.textMuted, fontSize: '11px' }}>
                              {invite.squad_name ? `Squad: ${invite.squad_name}` : 'General invite (no squad)'} • Expires: {new Date(invite.expires_at).toLocaleDateString()}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button onClick={() => copyInviteCode(invite.code)} style={{ background: THEME.bgHover, color: THEME.text, border: 'none', borderRadius: '6px', padding: '6px 10px', fontSize: '11px', cursor: 'pointer' }}>Copy</button>
                            <button onClick={() => handleRevokeInvite(invite.code)} style={{ background: THEME.danger, color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 10px', fontSize: '11px', cursor: 'pointer' }}>Revoke</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* New Invite Code Display */}
                {newInviteCode && (
                  <div style={{ background: `${THEME.success}15`, border: `1px solid ${THEME.success}30`, borderRadius: '12px', padding: '16px' }}>
                    <div style={{ color: THEME.success, fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>NEW INVITE CODE</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <code style={{ flex: 1, background: THEME.bg, padding: '12px', borderRadius: '8px', color: THEME.text, fontSize: '16px', fontWeight: '700', fontFamily: 'monospace' }}>{newInviteCode}</code>
                      <button onClick={() => copyInviteCode(newInviteCode)} style={{ background: THEME.success, color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 16px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>Copy</button>
                    </div>
                    <p style={{ color: THEME.textMuted, fontSize: '11px', margin: '10px 0 0' }}>Share this code with someone to invite them. Expires in 72 hours.</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'theme' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <div>
              <label style={{ display: 'block', color: THEME.textMuted, fontSize: '12px', marginBottom: '12px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Theme Mode</label>
              <div style={{ display: 'flex', gap: '10px' }}>
                {[
                  { id: 'dark', label: 'Dark', icon: '🌙', desc: 'Default dark theme' },
                  { id: 'light', label: 'Light', icon: '☀️', desc: 'Bright light theme' },
                  { id: 'oled', label: 'OLED', icon: '⚫', desc: 'Pure black for OLED' },
                ].map(mode => (
                  <button key={mode.id} onClick={() => setThemeMode(mode.id)} style={{
                    flex: 1, background: themeMode === mode.id ? myColor : THEME.bg, color: themeMode === mode.id ? '#000' : THEME.text,
                    border: `2px solid ${themeMode === mode.id ? myColor : THEME.border}`, borderRadius: '12px', padding: '16px 12px',
                    cursor: 'pointer', transition: 'all 0.15s', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px'
                  }}>
                    <span style={{ fontSize: '24px' }}>{mode.icon}</span>
                    <span style={{ fontWeight: '600', fontSize: '13px' }}>{mode.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label style={{ display: 'block', color: THEME.textMuted, fontSize: '12px', marginBottom: '12px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Accent Color</label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px' }}>
                <input type="color" value={accentColor} onChange={e => setAccentColor(e.target.value)} style={{ width: '52px', height: '52px', border: 'none', borderRadius: '10px', cursor: 'pointer', background: 'none' }} />
                <input type="text" value={accentColor} onChange={e => setAccentColor(e.target.value)} placeholder="#3b82f6" style={{ ...inputStyle, flex: 1 }} />
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#8b5cf6', '#f97316', '#84cc16'].map(c => (
                  <button key={c} onClick={() => setAccentColor(c)} style={{
                    width: '36px', height: '36px', borderRadius: '8px', background: c, border: accentColor === c ? '3px solid white' : 'none',
                    cursor: 'pointer', transition: 'transform 0.1s', boxShadow: accentColor === c ? `0 0 10px ${c}` : 'none'
                  }} />
                ))}
              </div>
            </div>
            {/* Sound Settings */}
            <div style={{ borderTop: `1px solid ${THEME.border}`, paddingTop: '18px' }}>
              <label style={{ display: 'block', color: THEME.textMuted, fontSize: '12px', marginBottom: '12px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Sound Effects</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '14px' }}>
                <button
                  onClick={() => { SoundService.setEnabled(!SoundService.isEnabled()); SoundService.click(); }}
                  style={{
                    background: SoundService.isEnabled() ? myColor : THEME.bg,
                    color: SoundService.isEnabled() ? '#000' : THEME.text,
                    border: `2px solid ${SoundService.isEnabled() ? myColor : THEME.border}`,
                    borderRadius: '10px', padding: '12px 20px', cursor: 'pointer', fontWeight: '600', fontSize: '13px',
                    display: 'flex', alignItems: 'center', gap: '8px'
                  }}
                >
                  {SoundService.isEnabled() ? '🔊' : '🔇'} {SoundService.isEnabled() ? 'Enabled' : 'Disabled'}
                </button>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ color: THEME.textMuted, fontSize: '12px' }}>🔉</span>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    defaultValue={SoundService.getVolume()}
                    onChange={e => { SoundService.setVolume(parseFloat(e.target.value)); SoundService.click(); }}
                    style={{ flex: 1, accentColor: myColor }}
                  />
                  <span style={{ color: THEME.textMuted, fontSize: '12px' }}>🔊</span>
                </div>
              </div>
              <button
                onClick={() => SoundService.success()}
                style={{ background: THEME.bg, color: THEME.textMuted, border: `1px solid ${THEME.border}`, borderRadius: '8px', padding: '8px 14px', fontSize: '12px', cursor: 'pointer', marginRight: '8px' }}
              >
                Test Sound
              </button>
            </div>

            <div style={{ padding: '14px', background: THEME.bg, borderRadius: '10px', border: `1px solid ${THEME.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '20px' }}>ℹ️</span>
                <p style={{ margin: 0, color: THEME.textMuted, fontSize: '13px' }}>Theme changes require a page reload to take effect.</p>
              </div>
            </div>
            <button onClick={() => {
              localStorage.setItem('themeMode', themeMode);
              localStorage.setItem('accentColor', accentColor);
              if (onThemeChange) onThemeChange(themeMode, accentColor);
              window.location.reload();
            }} style={{ background: myColor, color: '#000', border: 'none', borderRadius: '10px', padding: '14px', fontWeight: '600', cursor: 'pointer', fontSize: '15px' }}>
              Apply Theme
            </button>
          </div>
        )}

        {activeTab === 'integrations' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Steam Wishlist */}
            <div style={{ padding: '16px', background: THEME.bg, borderRadius: '12px', border: `1px solid ${THEME.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <span style={{ fontSize: '22px' }}>🎁</span>
                <div>
                  <h3 style={{ margin: 0, color: THEME.text, fontSize: '14px', fontWeight: '600' }}>Steam Wishlist</h3>
                  <p style={{ margin: '2px 0 0', color: THEME.textMuted, fontSize: '11px' }}>Sync wishlist + get price drop alerts</p>
                </div>
              </div>
              <button
                onClick={async () => {
                  setSaving(true); setError('');
                  try {
                    const result = await api.post('/api/integrations/steam/wishlist/sync', {});
                    setSuccess(`Synced ${result.total || 0} wishlist items!`);
                  } catch (err) { setError(err.message || 'Wishlist may be private'); }
                  finally { setSaving(false); }
                }}
                disabled={saving}
                style={{ width: '100%', background: '#1a9fff', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px', fontSize: '13px', fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer' }}
              >
                {saving ? 'Syncing...' : '🔄 Sync Wishlist'}
              </button>
            </div>

            {/* Discord Integration */}
            <div style={{ padding: '16px', background: THEME.bg, borderRadius: '12px', border: `1px solid ${discordStatus?.linked ? THEME.success : THEME.border}`, transition: 'border-color 0.2s' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <span style={{ fontSize: '22px' }}>🎮</span>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, color: THEME.text, fontSize: '14px', fontWeight: '600' }}>Discord</h3>
                  <p style={{ margin: '2px 0 0', color: THEME.textMuted, fontSize: '11px' }}>
                    {discordStatus?.linked ? 'Voice channels & server sync' : 'Connect for voice channels & server sync'}
                  </p>
                </div>
                {discordStatus?.linked && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: `${THEME.success}20`, padding: '4px 10px', borderRadius: '12px' }}>
                    <span style={{ color: THEME.success, fontSize: '14px' }}>✓</span>
                    <span style={{ color: THEME.success, fontSize: '12px', fontWeight: '600' }}>{discordStatus.discord_username || 'Linked'}</span>
                  </div>
                )}
              </div>
              {discordLoading ? (
                <div style={{ color: THEME.textMuted, fontSize: '12px', textAlign: 'center', padding: '10px' }}>Loading...</div>
              ) : discordStatus?.linked ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {currentSquad ? (
                    <button
                      onClick={async () => {
                        setCreatingVoice(true); setError('');
                        try {
                          const result = await api.post(`/api/integrations/discord/squad/${currentSquad.id}/voice`, { name: `${currentSquad.name} Gaming` });
                          if (result.web_link) {
                            window.open(result.web_link, '_blank');
                            setSuccess('Voice channel created!');
                          }
                        } catch (err) { setError(err.message || 'Squad not linked to Discord - configure in Squad Management'); }
                        finally { setCreatingVoice(false); }
                      }}
                      disabled={creatingVoice}
                      style={{ width: '100%', background: '#5865F2', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px', fontSize: '13px', fontWeight: '600', cursor: creatingVoice ? 'not-allowed' : 'pointer' }}
                    >
                      {creatingVoice ? 'Creating...' : `🔊 Start ${currentSquad.name} Voice Chat`}
                    </button>
                  ) : (
                    <p style={{ color: THEME.textMuted, fontSize: '12px', textAlign: 'center' }}>Select a squad to create voice channels</p>
                  )}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={async () => {
                        try {
                          const guilds = await api.get('/api/integrations/discord/guilds');
                          setDiscordGuilds(guilds.guilds || []);
                          setSuccess(`Found ${guilds.guilds?.length || 0} servers you can manage`);
                        } catch (err) { setError(err.message); }
                      }}
                      style={{ flex: 1, background: THEME.bgHover, color: THEME.text, border: `1px solid ${THEME.border}`, borderRadius: '8px', padding: '8px', fontSize: '12px', cursor: 'pointer' }}
                    >
                      View Servers
                    </button>
                    <button
                      onClick={async () => {
                        if (!window.confirm('Unlink Discord account?')) return;
                        try {
                          await api.delete('/api/integrations/discord/oauth/unlink');
                          setDiscordStatus({ linked: false });
                          setSuccess('Discord unlinked');
                        } catch (err) { setError(err.message); }
                      }}
                      style={{ background: THEME.bgHover, color: THEME.danger, border: `1px solid ${THEME.danger}`, borderRadius: '8px', padding: '8px 12px', fontSize: '12px', cursor: 'pointer' }}
                    >
                      Unlink
                    </button>
                  </div>
                  {discordGuilds.length > 0 && (
                    <div style={{ padding: '10px', background: THEME.bgCard, borderRadius: '8px', maxHeight: '150px', overflow: 'auto' }}>
                      <div style={{ color: THEME.textMuted, fontSize: '11px', marginBottom: '8px' }}>Your Discord Servers:</div>
                      {discordGuilds.map(g => (
                        <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px', borderRadius: '6px', background: THEME.bg, marginBottom: '4px' }}>
                          {g.icon_url && <img src={g.icon_url} alt="" style={{ width: 24, height: 24, borderRadius: '50%' }} />}
                          <span style={{ color: THEME.text, fontSize: '12px', flex: 1 }}>{g.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={async () => {
                    try {
                      const result = await api.get('/api/integrations/discord/oauth/auth-url');
                      if (result.url) {
                        window.open(result.url, '_blank');
                        startOAuthPolling('discord');
                      }
                      else setError(result.error || 'Discord OAuth not configured');
                    } catch (err) { setError(err.message); }
                  }}
                  style={{ width: '100%', background: '#5865F2', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px', fontSize: '13px', fontWeight: '600', cursor: oauthPolling ? 'wait' : 'pointer' }}
                >
                  {oauthPolling ? '⏳ Waiting for link...' : '🔗 Link Discord Account'}
                </button>
              )}
            </div>

            {/* Friend Activity */}
            <div style={{ padding: '16px', background: THEME.bg, borderRadius: '12px', border: `1px solid ${THEME.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '22px' }}>👥</span>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, color: THEME.text, fontSize: '14px', fontWeight: '600' }}>Steam Friends</h3>
                  <p style={{ margin: '2px 0 0', color: THEME.textMuted, fontSize: '11px' }}>See what your Steam friends are playing</p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const result = await api.get('/api/integrations/steam/friends');
                      setSuccess(`${result.playing_count || 0} friends playing, ${result.online_count || 0} online`);
                    } catch (err) { setError(err.message); }
                  }}
                  style={{ background: THEME.bgHover, color: THEME.text, border: `1px solid ${THEME.border}`, borderRadius: '8px', padding: '8px 12px', fontSize: '12px', cursor: 'pointer' }}
                >
                  Check
                </button>
              </div>
            </div>

            {/* Twitch */}
            <div style={{ padding: '16px', background: THEME.bg, borderRadius: '12px', border: `1px solid ${user?.twitch_username ? '#9146ff40' : THEME.border}`, transition: 'border-color 0.2s' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <span style={{ fontSize: '22px' }}>📺</span>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, color: THEME.text, fontSize: '14px', fontWeight: '600' }}>Twitch</h3>
                  <p style={{ margin: '2px 0 0', color: THEME.textMuted, fontSize: '11px' }}>Show when you're streaming to your squad</p>
                </div>
                {user?.twitch_username && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#9146ff30', padding: '4px 10px', borderRadius: '12px' }}>
                    <span style={{ color: '#9146ff', fontSize: '14px' }}>✓</span>
                    <span style={{ color: '#9146ff', fontSize: '12px', fontWeight: '600' }}>{user.twitch_username}</span>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={twitchUsername}
                  onChange={e => setTwitchUsername(e.target.value)}
                  placeholder="Your Twitch username"
                  style={{ ...inputStyle, flex: 1 }}
                />
                <button
                  onClick={async () => {
                    setSaving(true); setError('');
                    try {
                      await api.put('/api/profile', { twitch_username: twitchUsername || null });
                      setSuccess('Twitch username saved!');
                      onUpdate({ twitch_username: twitchUsername || null });
                    } catch (err) { setError(err.message); }
                    finally { setSaving(false); }
                  }}
                  disabled={saving}
                  style={{ background: '#9146ff', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 16px', fontSize: '13px', fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}
                >
                  {saving ? '...' : 'Save'}
                </button>
              </div>
              <p style={{ color: THEME.textDim, fontSize: '10px', margin: '8px 0 0' }}>
                Your stream status will show to squad members in the Twitch panel
              </p>
            </div>

            {/* Push Notifications */}
            <div style={{ padding: '16px', background: THEME.bg, borderRadius: '12px', border: `1px solid ${THEME.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <span style={{ fontSize: '22px' }}>🔔</span>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, color: THEME.text, fontSize: '14px', fontWeight: '600' }}>Push Notifications</h3>
                  <p style={{ margin: '2px 0 0', color: THEME.textMuted, fontSize: '11px' }}>Get notified when squad members are playing</p>
                </div>
              </div>
              {!('PushManager' in window) ? (
                <p style={{ color: THEME.textDim, fontSize: '12px', margin: 0 }}>Push notifications not supported in this browser</p>
              ) : (
                <button
                  onClick={handleTogglePush}
                  disabled={pushLoading}
                  style={{
                    width: '100%',
                    background: pushEnabled ? THEME.danger : THEME.success,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: pushLoading ? 'not-allowed' : 'pointer',
                    opacity: pushLoading ? 0.7 : 1
                  }}
                >
                  {pushLoading ? 'Loading...' : pushEnabled ? '🔕 Disable Notifications' : '🔔 Enable Notifications'}
                </button>
              )}
            </div>
          </div>
        )}

        {activeTab === 'account' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <div style={{ padding: '18px', background: THEME.bg, borderRadius: '12px', border: `1px solid ${THEME.border}` }}>
              <h3 style={{ margin: '0 0 12px', color: THEME.text, fontSize: '15px', fontWeight: '600' }}>Sync Steam Libraries</h3>
              <p style={{ color: THEME.textMuted, fontSize: '13px', margin: '0 0 14px' }}>Sync all games from Steam - shared co-op library plus both users' full personal libraries.</p>
              <button onClick={() => { onSync(); onClose(); }} disabled={syncing} className="press-effect" style={{ width: '100%', background: `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})`, color: '#000', border: 'none', borderRadius: '10px', padding: '14px', fontWeight: '600', cursor: syncing ? 'not-allowed' : 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                {syncing ? <><Spinner size={16} color="#000" /> Syncing...</> : '🔄 Sync All Libraries'}
              </button>
              <p style={{ color: THEME.textDim, fontSize: '11px', margin: '10px 0 0', textAlign: 'center' }}>Auto-syncs daily at midnight</p>
            </div>

            {/* Discord Webhook Integration */}
            <div style={{ padding: '18px', background: THEME.bg, borderRadius: '12px', border: `1px solid ${THEME.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <span style={{ fontSize: '24px' }}>📣</span>
                <div>
                  <h3 style={{ margin: 0, color: THEME.text, fontSize: '15px', fontWeight: '600' }}>Discord Notifications</h3>
                  <p style={{ margin: '2px 0 0', color: THEME.textMuted, fontSize: '12px' }}>Get notifications in your Discord channel</p>
                </div>
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', color: THEME.textMuted, fontSize: '11px', marginBottom: '8px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Webhook URL</label>
                <input
                  type="text"
                  value={discordWebhookUrl}
                  onChange={e => setDiscordWebhookUrl(e.target.value)}
                  placeholder="https://discord.com/api/webhooks/..."
                  style={inputStyle}
                />
                <p style={{ color: THEME.textDim, fontSize: '10px', margin: '6px 0 0' }}>
                  Create a webhook in Discord: Server Settings → Integrations → Webhooks
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={handleSaveWebhook} disabled={saving} style={{
                  flex: 1, background: myColor, color: '#000', border: 'none', borderRadius: '8px',
                  padding: '10px', fontSize: '13px', fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer'
                }}>
                  {saving ? 'Saving...' : 'Save Webhook'}
                </button>
                {discordWebhookUrl && (
                  <button onClick={handleTestWebhook} disabled={testingWebhook} style={{
                    background: THEME.bgHover, color: THEME.text, border: `1px solid ${THEME.border}`,
                    borderRadius: '8px', padding: '10px 16px', fontSize: '13px', fontWeight: '500',
                    cursor: testingWebhook ? 'not-allowed' : 'pointer'
                  }}>
                    {testingWebhook ? 'Sending...' : '🔔 Test'}
                  </button>
                )}
              </div>
            </div>

            {/* Change Password */}
            <div style={{ padding: '18px', background: THEME.bg, borderRadius: '12px', border: `1px solid ${THEME.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <span style={{ fontSize: '24px' }}>🔐</span>
                <div>
                  <h3 style={{ margin: 0, color: THEME.text, fontSize: '15px', fontWeight: '600' }}>Change Password</h3>
                  <p style={{ margin: '2px 0 0', color: THEME.textMuted, fontSize: '12px' }}>Update your account password</p>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', color: THEME.textMuted, fontSize: '11px', marginBottom: '6px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Current Password</label>
                  <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="Enter current password" style={inputStyle} />
                </div>
                <div>
                  <label style={{ display: 'block', color: THEME.textMuted, fontSize: '11px', marginBottom: '6px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>New Password</label>
                  <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Enter new password (min 6 chars)" style={inputStyle} />
                </div>
                <div>
                  <label style={{ display: 'block', color: THEME.textMuted, fontSize: '11px', marginBottom: '6px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Confirm New Password</label>
                  <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm new password" style={inputStyle} />
                </div>
                <button onClick={handleChangePassword} disabled={saving || !currentPassword || !newPassword} style={{
                  background: myColor, color: '#000', border: 'none', borderRadius: '8px', padding: '12px', fontWeight: '600',
                  cursor: (saving || !currentPassword || !newPassword) ? 'not-allowed' : 'pointer', opacity: (!currentPassword || !newPassword) ? 0.5 : 1, fontSize: '13px', marginTop: '4px'
                }}>{saving ? 'Changing...' : 'Change Password'}</button>
              </div>
            </div>

            <div style={{ borderTop: `1px solid ${THEME.border}`, paddingTop: '18px', marginTop: '6px' }}>
              <button onClick={onLogout} className="press-effect" style={{ width: '100%', background: THEME.danger, color: '#fff', border: 'none', borderRadius: '10px', padding: '14px', fontWeight: '600', cursor: 'pointer', fontSize: '14px' }}>
                🚪 Log Out
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default SettingsModal;
